import React, { memo } from 'react';
import { FiCheck, FiLoader } from 'react-icons/fi';

import type { UploadFileStatus } from './useQuickInferenceUpload';
import { formatFileSize, truncateFilename } from './quickInferenceUtils';

interface Props {
  progress: number;
  completedCount: number;
  totalCount: number;
  activeFileName: string | null;
  files: UploadFileStatus[];
}

function CircularProgress({ value }: { value: number }) {
  const size = 72;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#2563eb"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-200 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold tabular-nums text-slate-800 dark:text-white">
          {value}%
        </span>
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: UploadFileStatus['status'] }) {
  if (status === 'done') return <FiCheck size={12} className="text-emerald-500" />;
  if (status === 'uploading') return <FiLoader size={12} className="animate-spin text-blue-500" />;
  if (status === 'error') return <span className="text-[11px] font-bold text-red-500">!</span>;
  return <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />;
}

export const QuickInferenceUploadProgress = memo(function QuickInferenceUploadProgress({
  progress,
  completedCount,
  totalCount,
  activeFileName,
  files,
}: Props) {
  const displayProgress = Math.max(progress, activeFileName ? 3 : 0);

  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-slate-900/10 p-4">
      <div className="pointer-events-auto w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-4 px-5 py-5">
          <CircularProgress value={displayProgress} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">Uploading files</p>
            <p className="mt-0.5 truncate text-xs text-slate-500" title={activeFileName ?? undefined}>
              {activeFileName
                ? truncateFilename(activeFileName, 40)
                : 'Preparing upload…'}
            </p>
            <p className="mt-1 text-[11px] text-slate-400">
              {completedCount} of {totalCount} files complete
            </p>
          </div>
        </div>

        <ul className="max-h-32 divide-y divide-slate-100 overflow-y-auto border-t border-slate-100 dark:divide-gray-700 dark:border-gray-700">
          {files.map((file) => (
            <li
              key={file.uid}
              className={`flex items-center justify-between gap-3 px-4 py-2 text-xs ${
                file.status === 'uploading' ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
              }`}
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-700 dark:text-gray-200" title={file.name}>
                  {truncateFilename(file.name, 32)}
                </p>
                <p className="text-[10px] text-slate-400">{formatFileSize(file.size)}</p>
              </div>
              <StatusIcon status={file.status} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});
